import { FunctionComponent, useMemo, useState } from "react";
import { UseFormSetValue } from "react-hook-form";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { Toggle } from "@/components/Toggle/Toggle";
import { JobChangeIntervalUnit } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";

import { SaveListSettings } from "../../../schema/list";
import { DEFAULT_JOB_CHANGE_INTERVALS } from "../constants";

export interface ListJobChangeSettingsSectionProps {
	formValues: SaveListSettings;
	setValue: UseFormSetValue<SaveListSettings>;
	tsSettings: (key: string) => string;
}

const INTERVAL_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export const ListJobChangeSettingsSection: FunctionComponent<ListJobChangeSettingsSectionProps> = ({
	formValues,
	setValue,
	tsSettings,
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(true);

	const intervalNumberOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			INTERVAL_NUMBERS.map((num) => ({
				id: num,
				title: num.toString(),
			})),
		[]
	);

	const intervalUnitOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			Object.values(JobChangeIntervalUnit).map((unit) => ({
				id: unit,
				title: tsSettings(`jobChangeIntervalUnits.${unit}`),
			})),
		[tsSettings]
	);

	const findNumberOption = (id: number | null | undefined, fallback: number) =>
		intervalNumberOptions.find((o) => o.id === (id ?? fallback)) ??
		intervalNumberOptions[fallback - 1];

	const findUnitOption = (
		id: JobChangeIntervalUnit | null | undefined,
		fallback: JobChangeIntervalUnit
	) => intervalUnitOptions.find((o) => o.id === (id ?? fallback)) ?? intervalUnitOptions[0];

	const deactivationToggles = [
		[
			"jobChangeDeactivateOnChangedEmployer",
			formValues?.jobChangeDeactivateOnChangedEmployer ?? true,
			"jobChangeDeactivateChangedEmployer",
		],
		[
			"jobChangeDeactivateOnChangedRole",
			formValues?.jobChangeDeactivateOnChangedRole ?? false,
			"jobChangeDeactivateChangedRole",
		],
		[
			"jobChangeDeactivateOnAddedEmployer",
			formValues?.jobChangeDeactivateOnAddedEmployer ?? false,
			"jobChangeDeactivateAddedEmployer",
		],
		[
			"jobChangeDeactivateOnAddedRole",
			formValues?.jobChangeDeactivateOnAddedRole ?? false,
			"jobChangeDeactivateAddedRole",
		],
	] as const;

	const prioRows = [
		[
			"jobChangeCheckPrio1",
			"jobChangeIntervalPrio1",
			"jobChangeIntervalPrio1Unit",
			formValues?.jobChangeIntervalPrio1,
			formValues?.jobChangeIntervalPrio1Unit,
			DEFAULT_JOB_CHANGE_INTERVALS.prio1,
			DEFAULT_JOB_CHANGE_INTERVALS.prio1Unit,
		],
		[
			"jobChangeCheckPrio2",
			"jobChangeIntervalPrio2",
			"jobChangeIntervalPrio2Unit",
			formValues?.jobChangeIntervalPrio2,
			formValues?.jobChangeIntervalPrio2Unit,
			DEFAULT_JOB_CHANGE_INTERVALS.prio2,
			DEFAULT_JOB_CHANGE_INTERVALS.prio2Unit,
		],
		[
			"jobChangeCheckPrio3",
			"jobChangeIntervalPrio3",
			"jobChangeIntervalPrio3Unit",
			formValues?.jobChangeIntervalPrio3,
			formValues?.jobChangeIntervalPrio3Unit,
			DEFAULT_JOB_CHANGE_INTERVALS.prio3,
			DEFAULT_JOB_CHANGE_INTERVALS.prio3Unit,
		],
	] as const;

	return (
		<div
			className={classNames(
				"border p-6 mb-8 w-full items-start rounded-md border-1 border-border-light",
				"flex flex-col text-left gap-6",
				isOpen ? "bg-brand-white" : "bg-white"
			)}
		>
			<button
				className="w-full items-center group cursor-pointer flex flex-row text-left gap-6"
				type="button"
				onClick={() => setIsOpen(!isOpen)}
			>
				<div className="grow">
					<div className="text-smd text-text font-bold">{tsSettings("jobChangeSettings")}</div>
					<div className="text-ssm text-text-meta">
						{tsSettings("jobChangeSettingsDescription")}
					</div>
					<div className="text-sm text-text-disabled">
						{tsSettings("jobChangeSettingsDescriptionSubtitle")}
					</div>
				</div>
				<div
					className={classNames(
						"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
						isOpen ? "border-border-light bg-white" : "border-transparent bg-background-light"
					)}
				>
					<Icon
						className="text-text-meta group-hover:text-white w-[12px] h-auto"
						icon={isOpen ? faChevronUp : faChevronDown}
					/>
				</div>
			</button>

			{isOpen && (
				<div className="flex flex-col w-full">
					<div className="flex flex-col gap-4 w-full mb-8">
						{deactivationToggles.map(([field, isChecked, labelKey]) => (
							<div key={field} className="flex flex-row items-start gap-4">
								<Toggle
									isChecked={isChecked}
									onChange={() => {
										setValue(field, !formValues?.[field], {
											shouldDirty: true,
											shouldValidate: true,
										});
									}}
								/>
								<div className="flex flex-col">
									<span className="text-sm text-text">{tsSettings(labelKey)}</span>
								</div>
							</div>
						))}
					</div>

					<div className="flex flex-col gap-2 w-full mb-4">
						<div className="text-ssm font-medium text-text">
							{tsSettings("jobChangeTrackingFrequency")}
						</div>
					</div>

					<div className="border border-gray-200 rounded-lg mb-4">
						{prioRows.map(
							([
								labelKey,
								numField,
								unitField,
								currentNum,
								currentUnit,
								defaultNum,
								defaultUnit,
							]) => (
								<div
									key={numField}
									className="flex flex-row items-center border-b border-gray-200 p-4"
								>
									<div className="w-48 font-medium text-sm text-text">{tsSettings(labelKey)}</div>
									<div className="flex flex-row items-center gap-2">
										<span className="text-sm text-text-meta">{tsSettings("jobChangeEvery")}</span>
										<div className="w-20">
											<Dropdown
												data={intervalNumberOptions}
												defaultValue={findNumberOption(currentNum, defaultNum)}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== currentNum) {
														setValue(numField, value.id as number, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
											/>
										</div>
										<div className="w-28">
											<Dropdown
												data={intervalUnitOptions}
												defaultValue={findUnitOption(currentUnit, defaultUnit)}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== currentUnit) {
														setValue(unitField, value.id as JobChangeIntervalUnit, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
											/>
										</div>
										<span className="text-sm text-text-meta ml-4">
											{tsSettings("jobChangeCreditsPerRun")}
										</span>
									</div>
								</div>
							)
						)}
					</div>

					<div className="text-sm text-text-meta mb-4">{tsSettings("jobChangeDqNote")}</div>
				</div>
			)}
		</div>
	);
};
